import { useCallback, useEffect, useState } from "react";
import { DatePicker } from "@fluentui/react/lib/DatePicker";
import { Stack } from "@fluentui/react/lib/Stack";
import { TimePicker } from "@fluentui/react/lib/TimePicker";

import { isTrue } from "Utils/Boolean";
import { WithTheme } from "../Theme/WithTheme";
import { WithTranslator } from "../Translator";
import { useDateTimePickerStyles } from "./UseDateTimePickerStyles";
import { useDateConfig } from "./Hooks/UseDateConfig";
import { IDateTimePickerProps } from "./IDateTimePickerProps";

export const DateTimePicker = WithTheme(
  WithTranslator((props: IDateTimePickerProps): JSX.Element => {
    const {
      datePickerProps,
      disabled,
      errorMessage,
      isDateTime,
      translator,
      onChange,
      timePickerProps,
      value,
      theme: { spacing }
    } = props;
    const styles = useDateTimePickerStyles();

    const {
      strings, firstDayOfWeek, firstWeekOfYear, useHour12, dateTimeFormatter, formatDate
    } = useDateConfig();

    const [dateTime, setDateTime] = useState(value);
    useEffect(() => {
      setDateTime(value);
    }, [value]);

    const onSelectDate = useCallback(
      (v: Date) => {
        setDateTime((oldDateTime) => {
          const newDateTime = new Date(...getDateTime(v, oldDateTime));
          onChange(newDateTime);
          return newDateTime;
        });
      },
      [onChange]
    );

    const onSelectTime = useCallback(
      (_, v: Date) => {
        setDateTime((oldDateTime) => {
          const newDateTime = new Date(...getDateTime(oldDateTime, v));
          onChange(newDateTime);
          return newDateTime;
        });
      },
      [onChange]
    );

    return (
      <Stack horizontal tokens={{ childrenGap: spacing.s2 }}>
        <Stack.Item grow>
          <DatePicker
            dateTimeFormatter={dateTimeFormatter}
            disabled={disabled}
            firstDayOfWeek={firstDayOfWeek}
            firstWeekOfYear={firstWeekOfYear}
            formatDate={formatDate}
            highlightSelectedMonth
            onSelectDate={onSelectDate}
            placeholder={`${translator.get(`calendar.selectdate`, `Please select a date`)}...`}
            showGoToToday
            showWeekNumbers
            strings={strings}
            value={dateTime}
            {...(datePickerProps ?? {})}
          />
        </Stack.Item>
        {isTrue(isDateTime, false)
          ? (
            <Stack.Item disableShrink className={styles.time}>
              <TimePicker
                allowFreeform
                calloutProps={{ calloutMaxHeight: 600 }}
                defaultValue={dateTime}
                disabled={disabled ?? value == null}
                errorMessage={errorMessage}
                increments={15}
                onChange={onSelectTime}
                useComboBoxAsMenuWidth
                useHour12={useHour12}
                {...(timePickerProps ?? {})}
              />
            </Stack.Item>
          )
          : null}
      </Stack>
    );
  })
);

function getTime(date: Date, now: Date): [number, number] {
  return [date?.getHours() ?? now.getHours(), date?.getMinutes() ?? now.getMinutes()];
}

function getDate(date: Date, now: Date): [number, number, number] {
  return [
    date?.getFullYear() ?? now.getFullYear(),
    date?.getMonth() ?? now.getMonth(),
    date?.getDate() ?? now.getDate()
  ];
}

function getDateTime(dateValue, timeValue): [number, number, number, number, number] {
  const now = new Date();
  return [...getDate(dateValue, now), ...getTime(timeValue, now)];
}
